import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import InternalLinks from '@/components/seo/InternalLinks';
import {
  Sparkles, TrendingUp, Users, BarChart3, Target, Swords, LineChart,
  ArrowUpDown, Goal, ShieldCheck, ListChecks, FileText, Trophy
} from 'lucide-react';

const API_BASE = '';

function ContentBlock({ icon: Icon, title, content, color = "emerald", testId }) {
  if (!content || content.trim().length < 20) return null;
  const colors = {
    emerald: "border-emerald-500/20 bg-emerald-500/5",
    amber: "border-amber-500/20 bg-amber-500/5",
    blue: "border-blue-500/20 bg-blue-500/5",
    purple: "border-violet-500/20 bg-violet-500/5",
    rose: "border-rose-500/20 bg-rose-500/5",
    cyan: "border-cyan-500/20 bg-cyan-500/5",
    teal: "border-teal-500/20 bg-teal-500/5",
    orange: "border-orange-500/20 bg-orange-500/5",
  };
  const iconColors = {
    emerald: "text-emerald-400",
    amber: "text-amber-400",
    blue: "text-blue-400",
    purple: "text-violet-400",
    rose: "text-rose-400",
    cyan: "text-cyan-400",
    teal: "text-teal-400",
    orange: "text-orange-400",
  };

  return (
    <div className={`border rounded-xl p-4 ${colors[color] || colors.emerald}`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${iconColors[color] || iconColors.emerald}`} />
        <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider"
          style={{ fontFamily: 'var(--font-heading)' }}>{title}</h3>
      </div>
      <p className="text-sm text-slate-400 leading-relaxed">{content}</p>
    </div>
  );
}

export default function MatchSEOContent({ slug, matchStatus }) {
  const [seoData, setSeoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const { currentLang } = useLanguage();

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    fetch(`${API_BASE}/api/seo/match/${slug}?lang=${currentLang}`)
      .then(r => r.json())
      .then(data => {
        setSeoData(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [slug, currentLang]);

  if (loading || !seoData) return null;
  const blocks = seoData.content_blocks || {};

  // Don't show if explicitly fallback
  if (seoData.fallback) return null;

  // Check if there's any real content
  const hasContent = Object.values(blocks).some(v => typeof v === 'string' && v.trim().length >= 30)
  if (!hasContent) return null;

  // Define all possible blocks with their display config
  const BLOCK_CONFIG = [
    { key: 'match_overview', icon: BarChart3, titleKey: 'seo.matchOverview', color: 'blue' },
    { key: 'team_form_summary', icon: Users, titleKey: 'seo.teamForm', color: 'amber' },
    { key: 'h2h_summary', icon: Swords, titleKey: 'seo.headToHead', color: 'purple' },
    { key: 'odds_market_summary', icon: TrendingUp, titleKey: 'seo.oddsMarket', color: 'emerald' },
    { key: 'statistics_summary', icon: LineChart, titleKey: 'seo.statisticsSummary', color: 'cyan' },
    { key: 'odds_trend_analysis', icon: ArrowUpDown, titleKey: 'seo.oddsTrend', color: 'teal' },
    { key: 'goal_trend_summary', icon: Goal, titleKey: 'seo.goalTrend', color: 'orange' },
    { key: 'market_confidence', icon: ShieldCheck, titleKey: 'seo.marketConfidence', color: 'emerald' },
    { key: 'last_5_form', icon: ListChecks, titleKey: 'seo.last5Form', color: 'amber' },
    { key: 'pre_match_summary', icon: FileText, titleKey: 'seo.preMatch', color: 'blue' },
    { key: 'live_update_summary', icon: Target, titleKey: 'seo.liveUpdate', color: 'rose' },
    { key: 'post_match_recap', icon: Trophy, titleKey: 'seo.postMatchRecap', color: 'rose' },
  ];

  // Filter blocks based on match status
  const visibleBlocks = BLOCK_CONFIG.filter(b => {
    if (b.key === 'live_update_summary' && matchStatus !== 'live') return false;
    if (b.key === 'post_match_recap' && matchStatus !== 'finished') return false;
    if (b.key === 'pre_match_summary' && matchStatus !== 'prematch') return false;
    return blocks[b.key] && blocks[b.key].trim().length >= 20;
  });

  return (
    <div className="space-y-4 mb-6" data-testid="match-seo-content">
      {/* SEO Intro Paragraph */}
      {blocks.intro_paragraph && blocks.intro_paragraph.trim().length >= 30 && !blocks.intro_paragraph.startsWith('Compare odds') && (
        <div className="bg-slate-900/30 border border-slate-800/50 rounded-xl p-5" data-testid="seo-intro-block">
          <p className="text-sm text-slate-400 leading-relaxed">{blocks.intro_paragraph}</p>
        </div>
      )}

      {/* AI Insight Box - Premium feel */}
      {blocks.ai_insight && blocks.ai_insight.trim().length >= 20 && (
        <div className="relative overflow-hidden border border-emerald-500/30 bg-gradient-to-r from-emerald-500/5 to-cyan-500/5 rounded-xl p-5" data-testid="ai-insight-block">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-emerald-400" />
            <h3 className="text-sm font-semibold text-emerald-400 uppercase tracking-wider"
              style={{ fontFamily: 'var(--font-heading)' }}>{t('seo.aiInsight', 'AI Insight')}</h3>
          </div>
          <p className="text-sm text-slate-300 leading-relaxed font-medium">{blocks.ai_insight}</p>
        </div>
      )}

      {/* Content Grid - only renders non-empty blocks */}
      {visibleBlocks.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {visibleBlocks.map(b => (
            <ContentBlock
              key={b.key}
              icon={b.icon}
              title={t(b.titleKey, b.titleKey.split('.').pop()?.replace(/([A-Z])/g, ' $1').trim())}
              content={blocks[b.key]}
              color={b.color}
              testId={`seo-${b.key}-block`}
            />
          ))}
        </div>
      )}

      {/* Internal Links */}
      <InternalLinks links={seoData.internal_links} />
    </div>
  );
}
